import { Assets, Container, Sprite, TilingSprite, Texture, Rectangle } from 'pixi.js';
import { NATIVE_PX } from '../map/textures';
import { mulberry32 } from '../map/prng';
import { ARENA_TILE } from './render';
import { buildNavGrid, tileIndex, type NavGrid, type TileSource } from '../nav/grid';
import { dijkstraField, markChokepoints, markCover, opennessMap } from '../nav/fields';

/**
 * Arena battlefield — the ShroomLands + Volcano world (spec §11), at farm scale
 * (48px/tile). A shroom-grass field, a central Volcano Tower with a round safe zone
 * around it, giant mushrooms and volcanic rocks as cover, lava pools, and a torch ring.
 *
 * Placement is expressed as a LAYOUT (data) so it can be authored: `generateArenaLayout`
 * derives the default layout from a seed; a hand-authored layout (from the arena editor,
 * saved to localStorage) overrides it, and `buildArenaField` renders whichever it's given.
 * Each element's sprite is tagged with its layout element so the editor can drag/delete it.
 */

const PX = ARENA_TILE / NATIVE_PX;
const COLS = 80, ROWS = 80, SAFE_R = 8;   // bigger arena, divided by lava rivers
/**
 * Width, in tiles, of the unwalkable band around the map edge (see computeArenaBlocked).
 * Exported because the match view has to clamp hero positions to at LEAST this, and the
 * two numbers had drifted — a 1.5-tile clamp against a 2-tile wall pushed heroes into a
 * blocked cell, where the mover's collision gave up and froze them. Edge sticking.
 */
export const ARENA_BORDER_BAND = 2;
export const ARENA_LAYOUT_KEY = 'terraboma_arena_layout';

// Pack sprite paths.
const ART = {
  grass: '/pack/ShroomLands/Tiles/ShroomLands_Grass_Purple_Tiles.png',
  tower: '/pack/Volcano/Buildings/Volcano_Tower.png',
  shroomsA: '/pack/ShroomLands/Props/Mushrooms_Purple.png',
  shroomsB: '/pack/ShroomLands/Props/Mushrooms_Red.png',
  rocks: '/pack/Volcano/Volcano_Props/Volcano_Rocks.png',
  lava: '/pack/Volcano/Tiles/Volcano_lava_buble.png',   // 176×16 → 11 frames of 16px
  lavaTile: '/pack/Volcano/Tiles/Volcano_Tiles.png',    // molten-lava floor tile at (128,112)
  bridge: '/pack/Volcano/Tiles/Volcano_Bridge.png',     // 128×64 → vertical deck (col1) + horizontal deck (col5)
  torch: '/pack/Cute_Fantasy/Outdoor decoration/Big_Torch_Anim.png',  // 128×48 → 8 frames of 16×48 (ONE torch each)
};
const cropTile = (src: string, tx: number, ty: number): Texture => {
  const base = Texture.from(src);
  return new Texture({ source: base.source, frame: new Rectangle(tx * 16, ty * 16, 16, 16) });
};
// TilingSprite tiles the WHOLE atlas when given a framed sub-texture (Pixi gotcha → the lava
// tiles splash across the ground). Bake the one tile into a standalone 16×16 canvas texture so
// a TilingSprite repeats ONLY that tile.
const bakeTile = (src: string, tx: number, ty: number): Texture => {
  const res = Texture.from(src).source.resource as CanvasImageSource;
  const cv = document.createElement('canvas'); cv.width = 16; cv.height = 16;
  const ctx = cv.getContext('2d')!; ctx.imageSmoothingEnabled = false;
  try { ctx.drawImage(res, tx * 16, ty * 16, 16, 16, 0, 0, 16, 16); } catch { /* resource not drawable yet */ }
  return Texture.from(cv);
};
const sliceFrames = (src: string, fw: number, fh: number, count: number, row = 0): Texture[] => {
  const base = Texture.from(src);
  const out: Texture[] = [];
  for (let i = 0; i < count; i++) out.push(new Texture({ source: base.source, frame: new Rectangle(i * fw, row * fh, fw, fh) }));
  return out;
};

interface Anim { sprite: Sprite; frames: Texture[]; fps: number; t: number; i: number }

// ─── Layout: the authorable data model ───────────────────────────────────────
// 'sprite' = any pack sprite the editor placed (path + crop rect), so the arena can be
// dressed with ANY of the game's art, not just the built-in kinds.
export type ElKind = 'shroomA' | 'shroomB' | 'rock' | 'lava' | 'bridge' | 'torch' | 'sprite';
export interface ArenaEl { id: number; kind: ElKind; tx: number; ty: number; hot?: boolean; phase?: number; path?: string; sx?: number; sy?: number; sw?: number; sh?: number }
export const packURL = (p: string) => '/pack/' + p.split('/').map(encodeURIComponent).join('/');
export interface ArenaLayout {
  cols: number; rows: number;
  safe: { cx: number; cy: number; r: number };
  tower: { tx: number; ty: number };
  els: ArenaEl[];
  crossings?: number[];   // tile indices of carved stone crossings (render as clean path, no lava rim)
  walk?: number[];        // owner override: tile indices FORCED walkable (paint in the editor)
  wall?: number[];        // owner override: tile indices FORCED blocked
}

/**
 * Default layout for a seed: a big arena DIVIDED BY LAVA RIVERS (with periodic stone-bridge
 * crossings), giant mushrooms/rocks as cover, a central tower + safe zone, and one torch.
 * Rivers wander (random walk) like the sim map, never crossing the safe zone.
 */
export function generateArenaLayout(seed: number): ArenaLayout {
  const rng = mulberry32(seed >>> 0);
  const cols = COLS, rows = ROWS, cx = cols >> 1, cy = rows >> 1;
  const inSafe = (x: number, y: number) => Math.hypot(x - cx, y - cy) <= SAFE_R + 2;
  const occ = new Uint8Array(cols * rows);   // lava/bridge + placed cover
  const lavaSet = new Set<number>();         // lava tiles (for shore detection below)

  const els: ArenaEl[] = [];
  let id = 1;

  // ── Lava rivers with bridge crossings ──────────────────────────────────────
  // NARROW rivers (thickness 2–3) that run the FULL span from frame edge to frame edge —
  // each river connects to the map border (owner). A bridge crosses each river so the two
  // banks stay reachable. Bubbling cells (~1 in 12) get an animated lava-bubble overlay.
  const rivers = 2 + Math.floor(rng() * 2);
  for (let r = 0; r < rivers; r++) {
    const horizontal = rng() < 0.5;
    const span = horizontal ? cols : rows;
    const cross = horizontal ? rows : cols;
    let drift = Math.floor((0.25 + rng() * 0.5) * cross);
    const thickness = 2 + Math.floor(rng() * 2);
    const bridgeAt = Math.floor(span * (0.4 + rng() * 0.2));   // ONE bridge per river, near mid-span
    for (let a = 0; a < span; a++) {              // full span → both ends touch the frame
      drift += Math.floor(rng() * 3) - 1;
      drift = Math.max(1, Math.min(cross - 1 - thickness, drift));
      const isBridge = a === bridgeAt;
      for (let t = 0; t < thickness; t++) {          // lava is ALWAYS laid (continuous under bridges → red legs on lava)
        const x = horizontal ? a : drift + t, y = horizontal ? drift + t : a;
        if (x < 0 || y < 0 || x >= cols || y >= rows || inSafe(x, y)) continue;
        const i = tileIndex(x, y, cols);
        if (occ[i]) continue;
        els.push({ id: id++, kind: 'lava', tx: x, ty: y, phase: (x * 7 + y * 13) % 11 }); lavaSet.add(i); occ[i] = 1;
      }
      // One WHOLE bridge per crossing, spanning the river width. tx,ty = strip start; phase 1
      // = E–W river (vertical bridge, walk N↔S), phase 0 = N–S river (horizontal bridge, red
      // legs on the lava); sw = span length. The strip is made walkable at render time.
      if (isBridge) els.push({ id: id++, kind: 'bridge', tx: horizontal ? a : drift, ty: horizontal ? drift : a, phase: horizontal ? 1 : 0, sw: thickness });
    }
  }

  // ── Drown the necks: any non-lava cell hemmed by lava on OPPOSITE sides (or 3+ sides)
  // is a 1-wide stone sliver stranded IN the river — the "stone without logic in lava" the
  // owner rejected. Convert it to lava so rivers read as solid molten channels with no stone
  // islands, and so cover placed next uses the true (post-merge) shoreline. Iterate to a
  // fixpoint since drowning one neck can expose another.
  const isL = (x: number, y: number) => x >= 0 && y >= 0 && x < cols && y < rows && lavaSet.has(tileIndex(x, y, cols));
  for (let pass = 0; pass < 6; pass++) {
    let changed = false;
    for (let y = 1; y < rows - 1; y++) for (let x = 1; x < cols - 1; x++) {
      const i = tileIndex(x, y, cols);
      if (occ[i] || inSafe(x, y)) continue;
      const n = isL(x, y - 1), s = isL(x, y + 1), e = isL(x + 1, y), w = isL(x - 1, y);
      if ((n && s) || (e && w) || (Number(n) + Number(s) + Number(e) + Number(w) >= 3)) {
        els.push({ id: id++, kind: 'lava', tx: x, ty: y, phase: (x * 7 + y * 13) % 11 });
        lavaSet.add(i); occ[i] = 1; changed = true;
      }
    }
    if (!changed) break;
  }

  // ── Cover: giant mushrooms + volcanic rocks. Never on lava/bridge, never in the safe
  // zone, and never on a SHORE cell (any of the 8 neighbours is lava) — nothing sits on
  // the coastline. Also keeps 1 tile off the frame border.
  const onShore = (x: number, y: number) => {
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++)
      if (dx || dy) { const nx = x + dx, ny = y + dy; if (nx >= 0 && ny >= 0 && nx < cols && ny < rows && lavaSet.has(tileIndex(nx, ny, cols))) return true; }
    return false;
  };
  for (let i = 0; i < 100; i++) {
    const tx = 3 + Math.floor(rng() * (cols - 6)), ty = 3 + Math.floor(rng() * (rows - 6));
    if (inSafe(tx, ty) || occ[tileIndex(tx, ty, cols)] || onShore(tx, ty)) continue;
    const r = rng();
    els.push({ id: id++, kind: r < 0.4 ? 'shroomA' : r < 0.75 ? 'shroomB' : 'rock', tx, ty });
    occ[tileIndex(tx, ty, cols)] = 1;
  }
  // A single torch by the tower.
  els.push({ id: id++, kind: 'torch', tx: cx, ty: cy - 3, phase: Math.floor(rng() * 4) });

  // ── Connectivity repair LAST, so it sees lava, shore AND cover — carving stone
  // crossings (removing whatever lava/rock/mushroom is on the seam) until the whole
  // arena is reachable over clean cobblestone.
  const carved = carveArenaCrossings(cols, rows, cx, cy, els);
  return { cols, rows, safe: { cx, cy, r: SAFE_R }, tower: { tx: cx, ty: cy }, els, crossings: [...carved] };
}

/**
 * Connectivity repair. Full-span lava rivers can wall the arena into pockets that a
 * single bridge per river never links, leaving a quarter of the ground unreachable
 * ("can only move on part of the map"). This guarantees the WHOLE arena is reachable
 * over land: 0-1 BFS from the safe centre (stepping onto stone is free, crossing a
 * lava tile costs 1); any stone that needs a crossing is in a pocket, so the lava
 * along the cheapest seam back to the reachable side is carved into a stone crossing
 * (a 3-wide land bridge). Repeats until every stone tile is reachable. Mutates `els`
 * (removes the carved lava) and returns the carved tile indices. Idempotent.
 *
 * Runs at generation AND in buildArenaField, so authored/saved layouts are fixed too.
 */
export function carveArenaCrossings(cols: number, rows: number, cx: number, cy: number, els: ArenaEl[]): Set<number> {
  const N = cols * rows;
  const at2 = (x: number, y: number) => tileIndex(x, y, cols);
  const inBounds = (x: number, y: number) => x >= 2 && y >= 2 && x < cols - 2 && y < rows - 2;
  const lavaElAt = new Map<number, ArenaEl>();
  const coverAt = new Set<number>();   // mushrooms/rocks/sprites — solid, but carveable (removed) if on a seam
  const bridgeDeck = new Set<number>();   // player-built / generated bridges = already-walkable crossings
  for (const e of els) {
    if (e.kind === 'lava') lavaElAt.set(at2(e.tx, e.ty), e);
    else if (e.kind === 'shroomA' || e.kind === 'shroomB' || e.kind === 'rock') coverAt.add(at2(e.tx, e.ty));   // solid props (authored 'sprite' floors stay walkable)
    else if (e.kind === 'bridge') {
      const len = e.sw ?? 2, h = e.phase === 1;
      for (let t = -1; t <= len; t++) for (let w = -1; w <= 1; w++) {
        const bx = h ? e.tx + w : e.tx + t, by = h ? e.ty + t : e.ty + w;
        if (bx >= 0 && by >= 0 && bx < cols && by < rows) bridgeDeck.add(at2(bx, by));
      }
    }
  }
  const isL = (x: number, y: number) => x >= 0 && y >= 0 && x < cols && y < rows && lavaElAt.has(at2(x, y));
  // A SHORE tile (renders a lava rim) — must match the renderer's coastTile test, so
  // the carve knows shore is a wall it has to punch through, not free ground.
  const isShore = (x: number, y: number): boolean => {
    if (isL(x, y)) return false;
    const n = isL(x, y - 1), s = isL(x, y + 1), e = isL(x + 1, y), w = isL(x - 1, y);
    if ((n && s) || (e && w) || (Number(n) + Number(s) + Number(e) + Number(w) >= 3)) return false;
    if (n || s || e || w) return true;
    return isL(x + 1, y + 1) || isL(x - 1, y + 1) || isL(x + 1, y - 1) || isL(x - 1, y - 1);
  };
  // Walkable ground for connectivity = clean cobble (in bounds, not lava, not shore).
  // A crossing already carved is walkable too.
  const carved = new Set<number>();
  const isCover = (x: number, y: number) => coverAt.has(at2(x, y)) && !carved.has(at2(x, y));
  // Walkable already: clean cobble, a carved crossing, or a BRIDGE the player built.
  const isFree = (x: number, y: number) =>
    carved.has(at2(x, y)) || bridgeDeck.has(at2(x, y)) || (inBounds(x, y) && !isL(x, y) && !isShore(x, y) && !isCover(x, y));
  // A wall we may punch a crossing through — but NEVER a bridge tile (it's already a
  // crossing, so carving beside it just adds the redundant "bad sprite" paths).
  const carveable = (x: number, y: number) =>
    inBounds(x, y) && !bridgeDeck.has(at2(x, y)) && (isL(x, y) || isShore(x, y) || isCover(x, y)) && !carved.has(at2(x, y));

  for (let guard = 0; guard < 80; guard++) {
    // 0-1 BFS from the safe centre: onto cobble/crossing is free, punching through a
    // lava OR shore tile costs 1. The cheapest-crossing unreachable cobble tile marks
    // the nearest pocket; carve the lava+shore seam on its path into a walkable path.
    const dist = new Int32Array(N).fill(-1);
    const prev = new Int32Array(N).fill(-1);
    const start = at2(cx, cy);
    dist[start] = 0;
    const buckets: number[][] = [[start]];
    let worst = -1, worstDist = 0;
    for (let c = 0; c < buckets.length; c++) {
      const b = buckets[c]; if (!b) continue;
      for (const i of b) {
        if (dist[i] !== c) continue;
        const x = i % cols, y = (i / cols) | 0;
        if (isFree(x, y) && c > 0 && (worst < 0 || c < worstDist)) { worst = i; worstDist = c; }
        for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const) {
          if (!inBounds(nx, ny)) continue;
          const j = at2(nx, ny);
          const free = isFree(nx, ny);
          const cross = carveable(nx, ny);
          if (!free && !cross) continue;                     // border/tower-ish → impassable
          const nd = c + (free ? 0 : 1);
          if (dist[j] < 0 || nd < dist[j]) { dist[j] = nd; prev[j] = i; (buckets[nd] ??= []).push(j); }
        }
      }
    }
    if (worst < 0) break;                                    // every cobble tile reachable over land
    // Carve every lava/shore tile on the seam (and its carveable neighbours, for a
    // ~3-wide path that can't be pinched shut), turning them into a walkable crossing.
    const carveTile = (x: number, y: number) => { if (carveable(x, y)) carved.add(at2(x, y)); };
    for (let i = worst; i !== -1; i = prev[i]) {
      const x = i % cols, y = (i / cols) | 0;
      if (!carveable(x, y) && !carved.has(i)) continue;      // widen only around the seam
      carveTile(x, y);
      for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const) carveTile(nx, ny);
    }
  }
  // Remove any carved LAVA and COVER els → those tiles stop rendering molten / lose
  // their rock/mushroom and become the clean path. Carved SHORE tiles are already
  // stone; they're just marked walkable.
  const removeKinds = new Set<ArenaEl['kind']>(['lava', 'shroomA', 'shroomB', 'rock']);
  if (carved.size) for (let k = els.length - 1; k >= 0; k--) {
    const e = els[k];
    if (removeKinds.has(e.kind) && carved.has(at2(e.tx, e.ty))) els.splice(k, 1);
  }
  return carved;
}

/** A saved (authored) layout from the arena editor, or null. Safe on server/headless. */
export function loadSavedLayout(): ArenaLayout | null {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(ARENA_LAYOUT_KEY);
    if (!raw) return null;
    const l = JSON.parse(raw) as ArenaLayout;
    return l && Array.isArray(l.els) && l.cols === COLS && l.rows === ROWS ? l : null;
  } catch { return null; }
}

export interface ArenaField {
  cols: number; rows: number;
  safe: { cx: number; cy: number; r: number };
  blocked: Uint8Array;
  root: Container;
  heroLayer: Container;
  fxLayer: Container;
  layout: ArenaLayout;
  nodes: Map<number, Container>;   // element id → its sprite/container (editor: drag/delete)
  animate(dtMs: number): void;
  destroy(): void;
}

const TOWER_TILES_WIDE = 5;

/**
 * THE walkability rule, as one pure function.
 *
 * This used to exist twice: once inline in buildArenaField (what the game actually
 * walks on) and once hand-copied into field-connectivity.test.ts, whose comment
 * claimed it "mirrors the renderer's authoritative walkability pass exactly". Two
 * copies of a rule are one rule and one lie waiting to happen — the test could pass
 * on sixty seeds while the shipped grid walled the player in, because the test was
 * never reading the shipped grid. Both now call this.
 *
 * Everything is derived from the layout alone, so it needs no Pixi and no textures.
 */
export function computeArenaBlocked(L: ArenaLayout): Uint8Array {
  const { cols, rows } = L;
  const at = (x: number, y: number) => tileIndex(x, y, cols);
  const lava = new Set<number>();
  const cover = new Set<number>();
  const deck = new Set<number>();
  const spriteWalk = new Set<number>();
  for (const e of L.els) {
    if (e.kind === 'lava') lava.add(at(e.tx, e.ty));
    else if (e.kind === 'shroomA' || e.kind === 'shroomB' || e.kind === 'rock') cover.add(at(e.tx, e.ty));
    else if (e.kind === 'sprite') spriteWalk.add(at(e.tx, e.ty));
    else if (e.kind === 'bridge') {
      // The visible deck only: the river span plus one bank tile each end, 3 wide.
      const len = e.sw ?? 2, horiz = e.phase === 1;
      for (let t = -1; t <= len; t++) for (let w = -1; w <= 1; w++) {
        const bx = horiz ? e.tx + w : e.tx + t, by = horiz ? e.ty + t : e.ty + w;
        if (bx >= 0 && by >= 0 && bx < cols && by < rows) deck.add(at(bx, by));
      }
    }
  }
  const isL = (x: number, y: number) => x >= 0 && y >= 0 && x < cols && y < rows && lava.has(at(x, y));
  // A shore tile renders a lava rim, so it is a wall you cannot step onto.
  const isShore = (x: number, y: number): boolean => {
    if (isL(x, y)) return false;
    const n = isL(x, y - 1), s2 = isL(x, y + 1), e = isL(x + 1, y), w = isL(x - 1, y);
    if ((n && s2) || (e && w) || (Number(n) + Number(s2) + Number(e) + Number(w) >= 3)) return false;
    if (n || s2 || e || w) return true;
    return isL(x + 1, y + 1) || isL(x - 1, y + 1) || isL(x + 1, y - 1) || isL(x - 1, y - 1);
  };
  const crossings = new Set<number>(L.crossings ?? []);
  const forceWalk = new Set<number>(L.walk ?? []);
  const forceWall = new Set<number>(L.wall ?? []);
  const th = (TOWER_TILES_WIDE - 1) >> 1;
  const blocked = new Uint8Array(cols * rows);
  for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
    const i = at(x, y);
    if (forceWall.has(i)) { blocked[i] = 1; continue; }
    if (forceWalk.has(i)) { blocked[i] = 0; continue; }
    const walkway = crossings.has(i) || deck.has(i) || spriteWalk.has(i);
    const border = x < ARENA_BORDER_BAND || y < ARENA_BORDER_BAND || x >= cols - ARENA_BORDER_BAND || y >= rows - ARENA_BORDER_BAND;
    const tower = Math.abs(x - L.tower.tx) <= th && Math.abs(y - L.tower.ty) <= th;
    blocked[i] = !walkway && (border || lava.has(i) || isShore(x, y) || cover.has(i) || tower) ? 1 : 0;
  }
  return blocked;
}

/**
 * What fraction of the walkable ground a player standing in the safe zone can
 * actually reach, walking orthogonally.
 *
 * The arena is a last-one-standing fight on a field cut up by lava. If the spawn
 * ring is sealed, the match is not hard — it is broken, and the player just stands
 * there. Cheap enough to run on every build, which is the point: it turns "the map
 * is unplayable" from a bug report into something the code notices itself.
 */
export function arenaReachableFraction(blocked: Uint8Array, cols: number, rows: number, safe: { cx: number; cy: number }): number {
  const at = (x: number, y: number) => tileIndex(x, y, cols);
  // Start beside the tower, not on it — the tower footprint is solid.
  let sx = safe.cx, sy = safe.cy;
  for (let r = 3; r < 14 && blocked[at(sx, sy)]; r++) { sx = safe.cx + r; sy = safe.cy; }
  if (blocked[at(sx, sy)]) return 0;                    // nowhere to even stand
  const seen = new Uint8Array(cols * rows);
  const q = [at(sx, sy)];
  seen[q[0]] = 1;
  for (let h = 0; h < q.length; h++) {
    const i = q[h], x = i % cols, y = (i / cols) | 0;
    for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const) {
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const j = at(nx, ny);
      if (!seen[j] && !blocked[j]) { seen[j] = 1; q.push(j); }
    }
  }
  let walk = 0, reach = 0;
  for (let i = 0; i < blocked.length; i++) if (!blocked[i]) { walk++; if (seen[i]) reach++; }
  return walk === 0 ? 0 : reach / walk;
}

/** Below this, the arena is not a hard map — it is an unplayable one. */
export const ARENA_MIN_REACHABLE = 0.9;

/**
 * Which layout the match actually plays on.
 *
 * A layout SAVED in the arena editor lives in this browser's localStorage and was
 * being adopted by the live match silently, ahead of the seed map. Two things make
 * that dangerous, and together they are the "I cannot move out of the safe zone"
 * report:
 *   - painted `wall` tiles ALWAYS win in the walkability pass, and
 *   - an authored layout is deliberately NOT run through the connectivity carve
 *     (the owner builds their own crossings, and auto-cobblestone would fight the
 *     design).
 * So a layout that rings the spawn — or is simply mid-edit when it gets saved —
 * becomes a match nobody can leave the middle of. It is also per-browser, so it
 * cannot be reproduced by anyone else.
 *
 * A saved layout is therefore now CHECKED before it is trusted, and an unplayable
 * one is dropped for the seed map rather than shipped to a live match. An explicit
 * `layout` argument (the editor's own preview) is passed through untouched — that
 * caller wants to see exactly what it painted, walls and all.
 */
export function resolveArenaLayout(seed: number, layout?: ArenaLayout): ArenaLayout {
  if (layout) return layout;
  const saved = loadSavedLayout();
  if (!saved) return generateArenaLayout(seed);
  const reach = arenaReachableFraction(computeArenaBlocked(saved), saved.cols, saved.rows, saved.safe);
  if (reach >= ARENA_MIN_REACHABLE) return saved;
  console.warn(
    `[arena] Saved layout ignored: only ${Math.round(reach * 100)}% of it is reachable from the safe zone ` +
    `(need ${Math.round(ARENA_MIN_REACHABLE * 100)}%). Falling back to the generated map for seed ${seed}. ` +
    'Re-open the arena editor and carve a route out of the spawn, or press "Reset to seed" to clear it.',
  );
  return generateArenaLayout(seed);
}

/** Build the battlefield. `layout` overrides the seed default (authored arena). */
export async function buildArenaField(seed: number, layout?: ArenaLayout): Promise<ArenaField> {
  await Assets.load(Object.values(ART)).catch(() => undefined);
  const L = resolveArenaLayout(seed, layout);
  // Cobblestone crossings come ONLY from generation (L.crossings). We do NOT carve a
  // saved/authored layout — the owner builds their own crossings with bridge sprites,
  // and auto-cobblestone would fight their design. (Generated maps still self-repair
  // inside generateArenaLayout.)
  const crossings = new Set<number>(L.crossings ?? []);
  // Preload any authored pack sprites so their crops render crisp on first frame.
  const custom = [...new Set(L.els.filter((e) => e.kind === 'sprite' && e.path).map((e) => packURL(e.path!)))];
  if (custom.length) await Assets.load(custom).catch(() => undefined);
  const cols = L.cols, rows = L.rows;
  const blocked = new Uint8Array(cols * rows);
  for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++)
    if (x < 2 || y < 2 || x >= cols - 2 || y >= rows - 2) blocked[tileIndex(x, y, cols)] = 1;

  const root = new Container();
  // The volcanic STONE floor (Volcano_Tiles 2,2 — plain dark volcanic stone, NO lava cracks;
  // the 7,0 tile the safe zone used has glowing lava seams the owner rejected). The WHOLE
  // arena floor is this stone, same tile as the safe zone.
  const stoneTile = bakeTile(ART.lavaTile, 2, 1);   // floor_cobble
  const ground = new TilingSprite({ texture: stoneTile, width: cols * ARENA_TILE, height: rows * ARENA_TILE });
  ground.tileScale.set(PX);
  root.addChild(ground);

  const anims: Anim[] = [];
  const nodes = new Map<number, Container>();

  // Round STONE safe-zone floor (per-tile circle).
  const { cx, cy, r: safeR } = L.safe;
  const safeFloor = new Container();
  for (let ty = cy - safeR - 1; ty <= cy + safeR + 1; ty++) {
    for (let tx = cx - safeR - 1; tx <= cx + safeR + 1; tx++) {
      const d = Math.hypot(tx - cx, ty - cy);
      if (d > safeR + 0.5) continue;
      const t = new Sprite(stoneTile);
      t.scale.set(PX);
      t.x = tx * ARENA_TILE; t.y = ty * ARENA_TILE;
      t.tint = d > safeR - 0.6 ? 0xb0908c : 0xffffff;   // slightly darker rim, full stone inside
      safeFloor.addChild(t);
    }
  }
  root.addChild(safeFloor);
  root.enableRenderGroup?.();

  const heroLayer = new Container();
  heroLayer.sortableChildren = true;
  const lavaLayer = new Container();
  root.addChild(lavaLayer);
  // Coastline layer sits ABOVE the lava: grass lips overhang the molten edge.
  const coastLayer = new Container();
  root.addChild(coastLayer);
  // Bridges get their OWN layer, added to root AFTER the coastline rims (below), so
  // the deck sits ON TOP of the rim tiles instead of being half-swallowed by them —
  // the crossing reads as a solid walkway "brought forward", not a sunken plank.
  const bridgeLayer = new Container();

  // Element textures.
  // The first mushroom in each 128×96 sheet spans y≈2..51 — the old 32×48 crop cut its
  // base + shadow off. Take the full 32×52 cell so the whole sprite (incl. shadow) shows.
  const shroomA = new Texture({ source: Texture.from(ART.shroomsA).source, frame: new Rectangle(0, 0, 32, 52) });
  const shroomB = new Texture({ source: Texture.from(ART.shroomsB).source, frame: new Rectangle(0, 0, 32, 52) });
  const rockTex = new Texture({ source: Texture.from(ART.rocks).source, frame: new Rectangle(0, 0, 32, 50) });
  const lavaTile = new Texture({ source: Texture.from(ART.lavaTile).source, frame: new Rectangle(128, 112, 16, 16) });
  // Whole Volcano_Bridge halves (each 64×64 = 4 tiles): LEFT = vertical bridge (spans an E–W
  // river, walk N↔S); RIGHT = horizontal bridge with red legs (spans an N–S river, legs on lava).
  const bridgeVTex = new Texture({ source: Texture.from(ART.bridge).source, frame: new Rectangle(0, 0, 64, 64) });
  const bridgeHTex = new Texture({ source: Texture.from(ART.bridge).source, frame: new Rectangle(64, 0, 64, 64) });
  const torchFrames = sliceFrames(ART.torch, 16, 48, 8);   // 8 frames of ONE torch each
  const bubbleFrames = sliceFrames(ART.lava, 16, 16, 11);  // Volcano_lava_buble.png → 11 frames of 16px
  const lavaCells: ArenaEl[] = [];   // collected as lava els render; drives the roaming bubbles

  const blockAt = (tx: number, ty: number) => { if (tx >= 0 && ty >= 0 && tx < cols && ty < rows) blocked[tileIndex(tx, ty, cols)] = 1; };

  // Central Volcano Tower — centred in the middle of the safe zone (anchor 0.5,0.5 at the
  // safe centre), so the safe circle reads as a plaza around it.
  const tower = new Sprite(Texture.from(ART.tower));
  tower.anchor.set(0.5, 0.5);
  tower.scale.set((TOWER_TILES_WIDE * ARENA_TILE) / tower.texture.width);
  tower.x = (L.tower.tx + 0.5) * ARENA_TILE; tower.y = (L.tower.ty + 0.5) * ARENA_TILE;
  tower.zIndex = tower.y + tower.height / 2;   // y-sort by its base, not its centre
  (tower as unknown as { __kind: string }).__kind = 'tower';
  heroLayer.addChild(tower);
  nodes.set(0, tower);   // id 0 = the tower (editor drags it → updates layout.tower)

  // Collected during render, consumed by the authoritative walkability pass below.
  const coverTiles = new Set<number>();   // mushrooms / rocks (solid props)
  const bridgeDeck = new Set<number>();   // the walkable planks of each 'bridge' el (over lava)
  const spriteWalk = new Set<number>();   // authored 'sprite' tiles = owner's own bridge/floor → walkable OVER lava

  // Render each layout element, tagging its node for the editor.
  for (const el of L.els) {
    let node: Container;
    if (el.kind === 'lava') {
      const s = new Sprite(lavaTile); s.scale.set(PX);
      s.x = el.tx * ARENA_TILE; s.y = el.ty * ARENA_TILE;
      lavaLayer.addChild(s);
      blockAt(el.tx, el.ty); lavaCells.push(el);
      node = s;
    } else if (el.kind === 'bridge') {
      // Whole bridge image spanning the crossing; the lava under it stays (red legs on lava)
      // but the strip is made WALKABLE (unblock what the lava cells blocked).
      const len = el.sw ?? 2, horizRiver = el.phase === 1;
      // The walkable planks: the river span (t=0..len-1) plus one bank tile at each
      // end (t=-1, t=len), 3 tiles wide (w=-1..1) — EXACTLY the visible deck, no more.
      // (The old 5-wide, over-long strip unblocked lava off the sides of the bridge —
      // that was the "walkable lava" you saw beside crossings.)
      for (let t = -1; t <= len; t++) {
        for (let w = -1; w <= 1; w++) {
          const bx = horizRiver ? el.tx + w : el.tx + t, by = horizRiver ? el.ty + t : el.ty + w;
          if (bx >= 0 && by >= 0 && bx < cols && by < rows) bridgeDeck.add(tileIndex(bx, by, cols));
        }
      }
      const s = new Sprite(horizRiver ? bridgeVTex : bridgeHTex);
      s.anchor.set(0.5, 0.5);
      // Size to the crossing: (len+2) tiles ACROSS the river (spans it + one bank each side),
      // 3 tiles ALONG it — so the deck fits the channel instead of a giant 4×4 square.
      const across = (len + 2) * ARENA_TILE, along = 3 * ARENA_TILE;
      s.width = horizRiver ? along : across;      // horizRiver: bridge runs N–S, so E–W width = along
      s.height = horizRiver ? across : along;
      s.x = (horizRiver ? el.tx + 0.5 : el.tx + len / 2) * ARENA_TILE;
      s.y = (horizRiver ? el.ty + len / 2 : el.ty + 0.5) * ARENA_TILE;
      bridgeLayer.addChild(s);
      node = s;
    } else if (el.kind === 'torch') {
      const s = new Sprite(torchFrames[el.phase ? el.phase % torchFrames.length : 0]);
      s.anchor.set(0.5, 1); s.scale.set(PX * 1.1);   // base at the tile's centre → stands in the middle of the block
      s.x = (el.tx + 0.5) * ARENA_TILE; s.y = (el.ty + 0.5) * ARENA_TILE; s.zIndex = s.y;
      heroLayer.addChild(s);
      anims.push({ sprite: s, frames: torchFrames, fps: 8, t: (el.phase ?? 0) / 8, i: el.phase ?? 0 });
      node = s;
    } else if (el.kind === 'sprite' && el.path) {
      const sw = el.sw ?? 16, sh = el.sh ?? 16;
      const base = Texture.from(packURL(el.path));
      const tex = new Texture({ source: base.source, frame: new Rectangle(el.sx ?? 0, el.sy ?? 0, sw, sh) });
      // Centre the sprite on its OWN cell (floor/bridge tile), so the art lines up
      // with the single walkable cell — the old base-anchor shifted it up into the
      // cell behind, which is why the walkway and the visible planks disagreed.
      const s = new Sprite(tex); s.anchor.set(0.5, 0.5); s.scale.set(PX);
      s.x = (el.tx + 0.5) * ARENA_TILE; s.y = (el.ty + 0.5) * ARENA_TILE;
      // GROUND, not an entity: authored bridge/floor sprites go in bridgeLayer (below
      // the heroes) so you walk ON them — in heroLayer they y-sorted and you could
      // slip UNDER the next span of a long bridge.
      bridgeLayer.addChild(s);
      // Authored sprite tiles are WALKABLE and OVERRIDE the lava underneath — but ONLY
      // the el's OWN cell. The editor paints one el per cell, so a bridge (any length /
      // orientation) is drawn cell-by-cell and each painted cell becomes exactly one
      // walkable deck tile: no footprint-guessing, no over-marking the railing rows
      // (which let you walk on the lava behind), no gaps between stacked segments.
      spriteWalk.add(tileIndex(el.tx, el.ty, cols));
      node = s;
    } else {
      const tex = el.kind === 'shroomA' ? shroomA : el.kind === 'shroomB' ? shroomB : rockTex;
      const scale = el.kind === 'rock' ? 0.9 : 0.95;
      // Ground the prop's BASE at the bottom of its own tile (anchor 0.5,1 at ty+1),
      // so its stem/leg doesn't droop into the tile below — a hero standing just south
      // was overlapping the mushroom's leg ("standing on the shroom's leg"). zIndex at
      // the base y keeps the y-sort correct (heroes south draw in front, north behind).
      const s = new Sprite(tex); s.anchor.set(0.5, 1); s.scale.set(PX * scale);
      s.x = (el.tx + 0.5) * ARENA_TILE; s.y = (el.ty + 1) * ARENA_TILE; s.zIndex = s.y;
      heroLayer.addChild(s);
      // Block only the prop's own tile (the grounded anchor already keeps its leg on
      // that tile; blocking the tile below too made stones read as double-wide walls
      // and snagged movement around them).
      blockAt(el.tx, el.ty); coverTiles.add(tileIndex(el.tx, el.ty, cols));
      node = s;
    }
    (node as unknown as { __el: ArenaEl }).__el = el;
    nodes.set(el.id, node);
  }

  // ── Lava coastline: every stone cell that borders lava gets ONE lava-lake rim tile chosen
  // by its 8-neighbour lava pattern (the 12 tiles the owner hand-picked in the Lava Scenario
  // Board). Opaque, lava on the lava-facing side + basalt shore on the ground side. Two
  // adjacent lava sides → concave inner corner; one side → straight edge; lone diagonal →
  // convex outer corner. KNOWN GAP the owner is marking: diagonal bends still miss a clean
  // "connector" tile (a cell with one orthogonal edge + a diagonal that the neighbour turns).
  const COAST = {
    n: cropTile(ART.lavaTile, 8, 8), s: cropTile(ART.lavaTile, 8, 6),   // lava to N / to S
    e: cropTile(ART.lavaTile, 7, 7), w: cropTile(ART.lavaTile, 9, 7),   // lava to E / to W
    cvxNE: cropTile(ART.lavaTile, 7, 8), cvxNW: cropTile(ART.lavaTile, 9, 8),
    cvxSE: cropTile(ART.lavaTile, 7, 6), cvxSW: cropTile(ART.lavaTile, 9, 6),
    ccvNE: cropTile(ART.lavaTile, 11, 6), ccvNW: cropTile(ART.lavaTile, 10, 6),
    ccvSE: cropTile(ART.lavaTile, 11, 7), ccvSW: cropTile(ART.lavaTile, 10, 7),
  };
  const lavaSet = new Set(L.els.filter((e) => e.kind === 'lava').map((e) => tileIndex(e.tx, e.ty, cols)));
  const bridgeSet = new Set(L.els.filter((e) => e.kind === 'bridge').map((e) => tileIndex(e.tx, e.ty, cols)));
  const isLava = (x: number, y: number) => x >= 0 && y >= 0 && x < cols && y < rows && lavaSet.has(tileIndex(x, y, cols));
  const coastTile = (x: number, y: number): Texture | null => {
    const n = isLava(x, y - 1), s = isLava(x, y + 1), e = isLava(x + 1, y), w = isLava(x - 1, y);
    if ((n && s) || (e && w) || (Number(n) + Number(s) + Number(e) + Number(w) >= 3)) return null;
    if (s && e) return COAST.ccvSE; if (s && w) return COAST.ccvSW;
    if (n && e) return COAST.ccvNE; if (n && w) return COAST.ccvNW;
    if (n) return COAST.n; if (s) return COAST.s; if (e) return COAST.e; if (w) return COAST.w;
    if (isLava(x + 1, y + 1)) return COAST.cvxSE; if (isLava(x - 1, y + 1)) return COAST.cvxSW;
    if (isLava(x + 1, y - 1)) return COAST.cvxNE; if (isLava(x - 1, y - 1)) return COAST.cvxNW;
    return null;
  };
  for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
    if (isLava(x, y) || bridgeSet.has(tileIndex(x, y, cols))) continue;
    // Carved crossings are walkable paths THROUGH the lava — never dress them with a
    // lava rim (that made a walkable tile read as lava, "standing on lava"). Instead
    // give them a brighter cobble so the safe route across a river is obvious.
    if (crossings.has(tileIndex(x, y, cols))) {
      const p = new Sprite(stoneTile);
      p.scale.set(PX); p.x = x * ARENA_TILE; p.y = y * ARENA_TILE; p.tint = 0xd8cfc2;   // lightened = a clear path
      coastLayer.addChild(p);
      continue;
    }
    const tex = coastTile(x, y);
    if (!tex) continue;
    const s = new Sprite(tex);
    s.scale.set(PX); s.x = x * ARENA_TILE; s.y = y * ARENA_TILE; coastLayer.addChild(s);
  }
  // Bridges on top of the rims now (added after coastLayer → renders above it).
  root.addChild(bridgeLayer);

  // ── Authoritative walkability ────────────────────────────────────────────────
  // The single source of truth, matching EXACTLY what you see: a tile is walkable
  // ONLY if it is clean cobblestone (or a sanctioned crossing over lava — a bridge
  // deck or a carved path). Anything that shows lava — a lava cell OR a coastline
  // rim tile — is a wall you can't step onto ("only on cobblestone"). Cover props,
  // the tower footprint and the 2-tile frame border are walls too. This overwrites
  // the incremental grid so red-overlay == what's rendered, with no skipped lava.
  // ONE rule, shared with the connectivity test — see computeArenaBlocked. The pass
  // that used to live here was hand-copied into that test, so the two could drift
  // and the test would keep passing while the shipped grid walled the player in.
  // `L.crossings` is what `crossings` was built from, so the pure version sees the
  // same sanctioned paths.
  blocked.set(computeArenaBlocked(L));

  // ── Roaming lava bubbles: ~1 blob per 12 lava cells, but they HOP — each blob relocates
  // to a random lava cell every ~0.9s (staggered), so blooms travel across the field instead
  // of sitting still. Frames loop continuously; a fresh cell + phase is picked on each hop.
  interface Bubble { sprite: Sprite; i: number; t: number; hop: number; interval: number }
  const bubbles: Bubble[] = [];
  const bubbleN = Math.min(40, Math.max(1, Math.round(lavaCells.length / 12)));
  const pickCell = () => lavaCells.length ? lavaCells[Math.floor(Math.random() * lavaCells.length)] : null;
  for (let k = 0; k < bubbleN && lavaCells.length; k++) {
    const c = pickCell()!;
    const sp = new Sprite(bubbleFrames[k % bubbleFrames.length]); sp.scale.set(PX);
    sp.x = c.tx * ARENA_TILE; sp.y = c.ty * ARENA_TILE; lavaLayer.addChild(sp);
    bubbles.push({ sprite: sp, i: k % bubbleFrames.length, t: 0, hop: Math.random() * 0.9, interval: 0.7 + Math.random() * 0.6 });
  }

  const fxLayer = new Container();
  root.addChild(fxLayer);

  return {
    cols, rows, safe: { cx, cy, r: safeR }, blocked, root, heroLayer, fxLayer, layout: L, nodes,
    animate(dtMs: number) {
      const dt = dtMs / 1000;
      for (const a of anims) {
        a.t += dt;
        const adv = Math.floor(a.t * a.fps);
        if (adv > 0) { a.i = (a.i + adv) % a.frames.length; a.t -= adv / a.fps; a.sprite.texture = a.frames[a.i]; }
      }
      for (const b of bubbles) {
        b.t += dt;
        const adv = Math.floor(b.t * 6);
        if (adv > 0) { b.i = (b.i + adv) % bubbleFrames.length; b.t -= adv / 6; b.sprite.texture = bubbleFrames[b.i]; }
        b.hop += dt;
        if (b.hop >= b.interval) {   // jump to a new lava cell → the blob "moves"
          b.hop = 0; b.interval = 0.7 + Math.random() * 0.6;
          const c = pickCell();
          if (c) { b.sprite.x = c.tx * ARENA_TILE; b.sprite.y = c.ty * ARENA_TILE; }
        }
      }
    },
    destroy() { root.destroy({ children: true }); heroLayer.destroy({ children: true }); },
  };
}

export interface FieldNav {
  grid: NavGrid;
  distToSafe: Float64Array;
  chokepoints: number[];
  openness: Float32Array;
}

export function buildFieldNav(field: ArenaField): FieldNav {
  const src: TileSource = {
    w: field.cols, h: field.rows,
    blocked: (x, y) => field.blocked[tileIndex(x, y, field.cols)] === 1,
    cost: () => 1,
    flags: () => 0,
  };
  const grid = buildNavGrid(src);
  const chokepoints = markChokepoints(grid);
  markCover(grid);
  const safe: number[] = [];
  for (let y = 0; y < field.rows; y++) for (let x = 0; x < field.cols; x++)
    if (Math.hypot(x - field.safe.cx, y - field.safe.cy) <= field.safe.r && grid.walkable[tileIndex(x, y, field.cols)])
      safe.push(tileIndex(x, y, field.cols));
  return { grid, distToSafe: dijkstraField(grid, safe), chokepoints, openness: opennessMap(grid) };
}
